import { getExtensionValue } from "@medplum/core";
import { QuestionnaireItem, ResourceType } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

/**
 * Reads the search settings a `reference` QuestionnaireItem carries in its extensions.
 * @param questionnaireItem The reference item.
 * @param name Field name used in the error message for a misconfigured item.
 * @returns The resource type to search, its search criteria, and whether new entries can be typed.
 * @throws When the item has no resource type, or its search criteria lack a `${query}` variable.
 */
export function getQuestionnaireReferenceFieldConfig(
  questionnaireItem: QuestionnaireItem | undefined,
  name: string,
): QuestionnaireReferenceFieldConfig {
  const resourceType = getExtensionValue(
    questionnaireItem,
    Extensions.QuestionnaireItemReferenceResourceType,
  ) as ResourceType | undefined;
  if (!resourceType) {
    throw new Error(`[${name}] QuestionnaireItem of type reference must set a resourceType`);
  }

  const searchCriteria =
    (getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemReferenceSearchCriteria) as
      | string
      | undefined) ?? "";

  if (!searchCriteria) {
    throw new Error(`[${name}] QuestionnaireItem of type reference must set a searchCriteria`);
  }

  if (!searchCriteria.includes("${query}")) {
    throw new Error(`[${name}] searchCriteria must contain a $\{query} variable`);
  }

  const creatable =
    (getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemReferenceCreatable) as
      | boolean
      | undefined) ?? false;

  return { resourceType, searchCriteria, creatable };
}

export interface QuestionnaireReferenceFieldConfig {
  resourceType: ResourceType;
  searchCriteria: string;
  creatable: boolean;
}
